import { useEffect, useMemo, useState } from 'react';
import { playSound } from '../../utils/utils';
import VictoryChampion from './VictoryChampion';
import { getBossVictoryDie } from './boss';
import { getTotalShardsAcquired } from './shardTotals';
import './Victory.css';

const CEREMONY_DURATION = 4200;
const AWARDS = [
  {
    id: 'attack',
    label: 'HIGHEST ATTACK',
    unit: 'damage in a single hit',
    start: 450,
    duration: 850,
    sound: '/sounds/low-frequency-dice-impact.mp3',
  },
  {
    id: 'shards',
    label: 'TOTAL SHARDS ACQUIRED',
    unit: 'collected throughout this run',
    start: 1500,
    duration: 800,
  },
  {
    id: 'score',
    label: 'VICTORY SCORE',
    start: 2700,
    duration: 1200,
  },
];
const SHARDS = Array.from({ length: 7 }, (_, index) => ({
  x: (index % 2 ? -1 : 1) * (25 + index * 8),
  y: -30 - (index % 3) * 17,
}));

const Victory = ({ game, active = true, reduceMotion = false }) => {
  const [elapsed, setElapsed] = useState(0);
  const victoryDie = useMemo(() => getBossVictoryDie(game), [game]);
  useEffect(() => {
    if (!active) return undefined;
    if (reduceMotion) return undefined;
    let frame;
    let start = null;
    const played = new Set();
    const animate = (now) => {
      if (start === null) start = now;
      const time = Math.min(CEREMONY_DURATION, now - start);
      setElapsed(time);
      AWARDS.forEach((award) => {
        if (time >= award.start + award.duration && !played.has(award.id)) {
          played.add(award.id);
          if (award.sound) playSound(award.sound, { preventOverlap: true });
        }
      });
      if (time < CEREMONY_DURATION) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    const failsafe = setTimeout(
      () => setElapsed(CEREMONY_DURATION),
      CEREMONY_DURATION + 1500
    );
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(failsafe);
    };
  }, [active, reduceMotion]);
  const time = active && reduceMotion ? CEREMONY_DURATION : elapsed;
  const complete = active && time >= CEREMONY_DURATION;
  const values = {
    attack: game.runBestAttack || 0,
    shards: getTotalShardsAcquired(game),
    score: game.score || 0,
  };

  return (
    <div
      className={`coreboundVictory ${active ? 'is-active' : ''} ${
        complete ? 'is-complete' : ''
      } ${reduceMotion ? 'is-instant' : ''}`}
    >
      <VictoryChampion
        dieId={victoryDie.dieId}
        faceValues={victoryDie.faceValues}
        active={active}
        reduceMotion={reduceMotion}
      />
      <span className="coreboundPanelKicker">
        SYSTEM LIBERATED · ALL TEN SECTORS CLEARED
      </span>
      <h2>The Core is broken!</h2>
      <div
        className="coreboundVictoryCeremony"
        role="group"
        aria-label="Victory run highlights"
      >
        {AWARDS.map((award) => {
          const progress = Math.max(
            0,
            Math.min(1, (time - award.start) / award.duration)
          );
          const count = Math.round(
            values[award.id] * (1 - (1 - progress) ** 3)
          );
          return (
            <div
              key={award.id}
              className={`coreboundVictoryStat ${
                active && time >= award.start ? 'is-revealed' : ''
              } ${progress === 1 ? 'is-locked' : ''}`}
              data-award={award.id}
            >
              <span>{award.label}</span>
              <span className="coreboundSrOnly">{values[award.id]}</span>
              <strong aria-hidden="true">{count.toLocaleString()}</strong>
              {award.unit && <small>{award.unit}</small>}
              {award.id === 'shards' && (
                <div className="coreboundVictoryShardGather" aria-hidden="true">
                  {SHARDS.map((shard, index) => (
                    <i
                      key={index}
                      style={{
                        '--shard-x': `${shard.x}px`,
                        '--shard-y': `${shard.y}px`,
                        '--shard-delay': `${index * 0.065}s`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="coreboundVictoryMessage">
        The stars are coming back because of you.
      </p>
      <div className="coreboundVictoryHonors">
        <span className="coreboundVictoryTitle">✦ COREBREAKER</span>
        <span>
          {game.bossRound} battle rounds · {game.relics.length} relics in your
          arsenal
        </span>
      </div>
      {game.bossBattleStats && (
        <dl
          className="coreboundBossVictoryRecap"
          aria-label="Core battle highlights"
        >
          <div>
            <dt>BIGGEST CORE HIT</dt>
            <dd>{game.bossBattleStats.biggestHit} damage</dd>
          </div>
          <div>
            <dt>DAMAGE PREVENTED</dt>
            <dd>{game.bossBattleStats.prevented} HP</dd>
          </div>
          <div>
            <dt>CLEAN DEFENSES</dt>
            <dd>{game.bossBattleStats.cleanDefenses}</dd>
          </div>
        </dl>
      )}
      <div className="coreboundVictoryCeremonyFooter">
        <span
          className={`coreboundVictoryRunSeal ${complete ? 'is-revealed' : ''}`}
        >
          {game.newBestScore ? '✦ NEW PERSONAL BEST' : '✓ VICTORY RECORDED'}
        </span>
      </div>
      <p className="coreboundSrOnly" role="status">
        {complete
          ? `Victory recorded. Highest attack: ${values.attack}. Total Shards Acquired: ${values.shards}. Run score: ${values.score}.`
          : ''}
      </p>
    </div>
  );
};

export default Victory;
